import React from "react";
import { useNavigate } from "react-router-dom";
import { Shield, MonitorSmartphone, Database, CreditCard, ChevronRight, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { SCard, SRow, VerifiedBadge, fmtDateFr, relativeFrTime } from "./SettingsUI";
import { useLang, translateFr as tfr } from "@/i18n";

const NavCard = ({ icon: Icon, title, desc, to, testid, children }) => {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(to)} data-testid={testid}
      className="text-left rounded-2xl border border-white/[0.07] bg-surface p-5 hover:border-white/[0.16] transition-colors group w-full">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-xl bg-white/[0.04] border border-white/[0.07] flex items-center justify-center">
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-xs text-muted-foreground truncate">{desc}</p>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-70 transition-opacity" />
      </div>
      {children}
    </button>
  );
};

export const SectionHome = ({ overview }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t, tf } = useLang();
  const p = overview?.profile || {};
  const s = overview?.security || {};
  const sub = overview?.subscription || {};
  const subLabel = tf({ trialing: "Essai gratuit", active: "Actif", past_due: tfr("Paiement en retard"), canceled: tfr("Annulé"), expired: tfr("Expiré") }[sub.status] || sub.status);

  return (
    <div className="space-y-4 min-w-0" data-testid="settings-home">
      <SCard testid="account-summary-card"
        right={<Button variant="outline" size="sm" onClick={() => navigate("/settings/account")} data-testid="summary-edit-btn">{tf("Modifier")}</Button>}>
        <div className="flex items-center gap-4">
          {p.picture ? (
            <img src={p.picture} alt="" className="h-14 w-14 rounded-full object-cover border border-white/[0.1]" />
          ) : (
            <div className="h-14 w-14 rounded-full bg-red-500/15 text-red-400 flex items-center justify-center text-lg font-semibold">
              {(p.first_name?.[0] || p.name?.[0] || "?").toUpperCase()}{(p.last_name?.[0] || "").toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-base font-semibold truncate" data-testid="summary-name">{p.name || "—"}</p>
            <p className="text-xs text-muted-foreground capitalize">{p.role === "agent" ? tf("Agent sportif") : p.role}</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-8 mt-4">
          <SRow label="Email" value={p.email} badge={<VerifiedBadge verified={p.email_verified} />} testid="summary-email" />
          <SRow label={tf("Téléphone")} value={p.phone || tf("Non renseigné")} badge={p.phone ? <VerifiedBadge verified={p.phone_verified} /> : null} testid="summary-phone" />
          <SRow label={tf("Langue")} value={p.preferred_language === "en" ? "English" : "Français"} testid="summary-language" />
          <SRow label={tf("Fuseau horaire")} value={p.timezone} testid="summary-timezone" />
          <SRow label={tf("Membre depuis")} value={fmtDateFr(p.created_at)} testid="summary-created" />
          <SRow label={tf("Connexion")} value={p.auth_provider === "google" ? "Google" : tf("Email / mot de passe")} testid="summary-provider" />
        </div>
      </SCard>

      <div className="grid md:grid-cols-2 gap-4">
        <NavCard icon={Shield} title={tf("Sécurité")} desc={tf("Mot de passe, double authentification")} to="/settings/security" testid="home-card-security">
          <div className="text-xs text-muted-foreground space-y-1.5">
            <p>{tf("Mot de passe")} · {s.has_password ? (s.password_changed_at ? `${tf("modifié le")} ${fmtDateFr(s.password_changed_at)}` : tf("défini")) : tf("connexion Google")}</p>
            <p>{tf("Double authentification")} · {s.two_factor_enabled ? <span className="text-green-500">{tf("activée")}</span> : tf("non activée")}</p>
          </div>
        </NavCard>
        <NavCard icon={MonitorSmartphone} title={tf("Sessions & appareils")} desc={tf("Appareils connectés à votre compte")} to="/settings/sessions" testid="home-card-sessions">
          <p className="text-xs text-muted-foreground">{s.sessions_count} {tf("session")}{s.sessions_count > 1 ? "s" : ""} {tf("active")}{s.sessions_count > 1 ? "s" : ""}</p>
        </NavCard>
        <NavCard icon={CreditCard} title={tf("Abonnement")} desc={tf("Plan, facturation et paiement")} to="/settings/billing" testid="home-card-billing">
          <p className="text-xs text-muted-foreground">{tf("Statut")} · <span className={sub.status === "active" ? "text-green-500" : "text-amber-400"}>{subLabel}</span></p>
        </NavCard>
        <NavCard icon={Database} title={tf("Données & confidentialité")} desc={tf("Export, confidentialité, suppression")} to="/settings/data" testid="home-card-data">
          <p className="text-xs text-muted-foreground">{tf("Téléchargez vos données · gérez votre compte")}</p>
        </NavCard>
      </div>

      {(overview?.recent_security_events || []).length > 0 && (
        <SCard title={tf("Activité de sécurité récente")} testid="home-security-events">
          <div className="space-y-2">
            {overview.recent_security_events.slice(0, 5).map((e, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-xs">
                <span className="text-muted-foreground truncate">{e.label}</span>
                <span className="text-muted-foreground/60 shrink-0">{relativeFrTime(e.created_at)}</span>
              </div>
            ))}
          </div>
        </SCard>
      )}

      <div className="flex justify-end">
        <Button variant="ghost" onClick={logout} className="text-red-400 hover:text-red-400 gap-2" data-testid="settings-logout-btn">
          <LogOut className="h-4 w-4" /> {t("nav.logout")}
        </Button>
      </div>
    </div>
  );
};
